import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { PaginationData } from '../context/PaginationContext'

function usePaginationEffect(
  paginationData: PaginationData,
  updateQueryParams: (updatePaginationData: PaginationData) => void,
  loadData: () => void,
  setHasMounted: (hasMounted: boolean) => void,
  initialPaginationData: PaginationData,
) {
  const hasMountedRef = useRef(false)
  const location = useLocation()

  useEffect(() => {
    if (!location.search) {
      updateQueryParams({
        ...initialPaginationData,
      })
    }
  }, [])

  useEffect(() => {
    if (hasMountedRef.current) {
      if (paginationData) {
        loadData()
      }
    } else {
      hasMountedRef.current = true
      setHasMounted(true)
    }
  }, [
    paginationData?.page,
    paginationData?.size,
    paginationData?.searchQuery,
    paginationData?.sort,
    paginationData?.filters,
  ])

  const updatePaginationData = <K extends keyof PaginationData>(
    key: K,
    newValue: PaginationData[K],
    setPageToZero = false,
  ) => {
    const resetPage = setPageToZero ? { page: 0 } : {}
    const updatedPaginationData = {
      ...paginationData,
      ...resetPage,
      [key]: newValue,
    }
    updateQueryParams(updatedPaginationData)
  }

  const handleSortChange = (columnName: string, direction: string) => {
    if (!!columnName && !!direction && hasMountedRef.current) {
      updatePaginationData('sort', [columnName, direction].join(','))
    }
  }

  const handlePrev = () => {
    if (paginationData.page > 0) {
      updatePaginationData('page', paginationData.page - 1)
    }
  }

  const handleNext = () => {
    if (
      paginationData.totalPages &&
      paginationData.page < paginationData.totalPages - 1
    ) {
      updatePaginationData('page', paginationData.page + 1)
    }
  }

  const handleSearch = (searchQuery: string) => {
    updatePaginationData('searchQuery', searchQuery, true)
  }

  const handleSizeChange = (size: number) => {
    updatePaginationData('size', size, true)
  }

  return {
    handleNext,
    handleSearch,
    handleSizeChange,
    handleSortChange,
    handlePrev,
    updatePaginationData,
  }
}

export default usePaginationEffect
